/**
 * Passport review nudge — surfaces critical financial documents that
 * should be looked at again (wills, life insurance, risk cover,
 * pension) and prompts the consumer to review them now.
 *
 * Purely client-side — reads the wallet's document vault and applies
 * category-specific staleness rules. Missing critical categories are
 * flagged too so the consumer is nudged to upload.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CalendarClock, FileText } from "lucide-react";
import { usePassportReviewItems, formatAge } from "@/lib/wallet-passport-review";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

export default function PassportReviewNudge() {
  const state = usePassportReviewItems();
  const [dismissed, setDismissed] = useState(false);

  if (!state.loaded || dismissed) return null;
  const total = state.overdue.length + state.missing.length;
  if (total === 0) return null;

  return (
    <section
      data-testid="passport-review-nudge"
      className="rounded-2xl border border-amber-200 bg-amber-50 p-5 space-y-3"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: NAVY }}>
          <AlertTriangle className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>
            {total === 1
              ? "1 critical document needs your attention"
              : `${total} critical documents need your attention`}
          </div>
          <p className="text-[13px] text-amber-900 mt-1">
            Life changes — a new dependant, a new home, a new job — often mean the small print no longer fits.
            Would you like to review them now?
          </p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          data-testid="review-nudge-dismiss"
          className="text-[11px] text-slate-500 hover:text-slate-800 shrink-0"
        >
          Later
        </button>
      </div>

      <ul className="divide-y divide-amber-200 rounded-lg bg-white/60">
        {state.overdue.map((item) => (
          <li key={`o-${item.document_id}`} className="flex items-center gap-3 px-3 py-2 text-[12.5px]" data-testid={`review-overdue-${item.category}`}>
            <CalendarClock className="w-3.5 h-3.5 text-amber-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold" style={{ color: NAVY }}>{item.label}</span>
              <span className="text-slate-500"> · last updated {formatAge(item.age_months)} ago</span>
              {item.institution && <span className="text-slate-400"> · {item.institution}</span>}
            </div>
          </li>
        ))}
        {state.missing.map((item) => (
          <li key={`m-${item.category}`} className="flex items-center gap-3 px-3 py-2 text-[12.5px]" data-testid={`review-missing-${item.category}`}>
            <FileText className="w-3.5 h-3.5 text-amber-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold" style={{ color: NAVY }}>{item.label}</span>
              <span className="text-slate-500"> · nothing on file yet — worth uploading</span>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-3">
        <Link
          to="/wallet/documents"
          data-testid="review-nudge-cta"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-semibold text-white"
          style={{ background: NAVY }}
        >
          Yes, review now <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <span className="text-[11px]" style={{ color: GOLD }}>Fair-conduct nudge</span>
      </div>
    </section>
  );
}
